import { Card, Heading, Text, ThemeToggle } from "@microbeta/ui";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { paths } from "../../../../shared/utils/routing";
import { useAuthContext } from "../../AuthContext";
import background from "../../assets/images/background.svg";
import { RecaptchaNotice } from "../../components/RecaptchaNotice";
import { SignUpForm } from "./SignUpForm";
import styles from "./signup-with-email.module.scss";

interface CreateUserRequest {
  email: string;
  password: string;
  fullName: string;
}

const SignUpWithEmail = () => {
  const { t } = useTranslation();
  const { signUp } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirectUrl");

  const handleSignUpUser = async (values: CreateUserRequest) => {
    try {
      await signUp(values.email, values.password, values.fullName);
      const onboardingUrl = redirectUrl
        ? `/onboarding?redirectUrl=${encodeURIComponent(redirectUrl)}`
        : "/onboarding";
      setTimeout(() => navigate(onboardingUrl), 0);
    } catch (error: unknown) {
      console.error("Registration failed:", error);

      // The AuthContext already handles toast notifications for signup errors,
      // so we don't need to show another toast here. The error is automatically
      // displayed with proper translations through the AuthContext.
    }
  };

  return (
    <div className={styles.container}>
      <img
        src={background}
        className={styles.backgroundImg}
        alt=""
        aria-hidden="true"
      />

      {/* Theme Toggle - positioned in top-right corner */}
      <div className={styles.themeToggleContainer}>
        <ThemeToggle size="md" />
      </div>

      <Card className={styles.card}>
        <Heading variant="medium" className={styles.title}>
          {t("signUp.title")}
        </Heading>
        <Text variant="large" className={styles.subtitle}>
          {t("signUp.subtitle")}
        </Text>
        <SignUpForm onSubmit={handleSignUpUser} isLoading={false} />

        <Text variant="small" className={styles.signInLinkContainer}>
          {t("signUpForm.alreadyHaveAccount")}
          <Link to={paths.signin} className={styles.signInLink}>
            {t("signUpForm.signInHere")}
          </Link>
        </Text>
        <RecaptchaNotice />
      </Card>
    </div>
  );
};

export default SignUpWithEmail;
